import { useState, useRef, useEffect } from 'react';
import { cloudfrontUrl } from '../../constants/imageUrl';

const icons = [
  `${cloudfrontUrl}/bag_icon/checkin-removebg-preview.png`,
  `${cloudfrontUrl}/bag_icon/checkin_2-removebg-preview.png `,
  `${cloudfrontUrl}/bag_icon/checkin_3-removebg-preview.png`,
];

const uberEatsIcon = `${cloudfrontUrl}/delivery/ubereats-logo.png`;
const deliverooIcon = `${cloudfrontUrl}/delivery/deliveroo-logo.png`;

export default function DeliveryIcon() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [randomIcon] = useState(
    () => icons[Math.floor(Math.random() * icons.length)],
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setHasMoved(true);
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && e.touches[0]) {
        setHasMoved(true);
        setPosition({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, dragStart]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isExpanded) return;

    setHasMoved(false);

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      if (position) {
        // Already has custom position, use it
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      } else {
        // First drag, use current element position
        setDragStart({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    }

    setIsDragging(true);
    e.preventDefault();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isExpanded) return;

    setHasMoved(false);

    if (containerRef.current && e.touches[0]) {
      const rect = containerRef.current.getBoundingClientRect();
      const touch = e.touches[0];

      if (position) {
        // Already has custom position, use it
        setDragStart({
          x: touch.clientX - position.x,
          y: touch.clientY - position.y,
        });
      } else {
        // First drag, use current element position
        setDragStart({
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        });
      }
    }

    setIsDragging(true);
    e.preventDefault();
  };

  const handleIconClick = (e: React.MouseEvent) => {
    if (!hasMoved) {
      setIsExpanded(true);
    }
    e.stopPropagation();
  };

  const handleDeliveryChoice = (service: 'ubereats' | 'deliveroo') => {
    const urls = {
      ubereats:
        'https://www.ubereats.com/store-browse-uuid/7a6ca948-a346-4efd-9b86-111ea61f137c?diningMode=DELIVERY',
      deliveroo:
        'https://deliveroo.fr/menu/nice/saint-laurent-du-var-var/korean-street-market-avenue-eugene-donadei-cap3000?utm_campaign=organic&utm_medium=referrer&utm_source=menu_share',
    };
    window.open(urls[service], '_blank');
    setIsExpanded(false);
  };

  return (
    <div
      ref={containerRef}
      className="fixed z-50"
      style={{
        left: position ? `${position.x}px` : 'auto',
        top: position ? `${position.y}px` : 'auto',
        bottom: !position ? '1.5rem' : 'auto',
        right: !position ? '0' : 'auto',
      }}
    >
      {isExpanded ? (
        <div className="relative w-[30vw] h-[30vw] lg:w-[15vw] lg:h-[15vw]">
          <button
            onClick={() => handleDeliveryChoice('ubereats')}
            className="absolute bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border-2 border-transparent hover:border-green-400 w-[15vw] h-[15vw] lg:w-[5vw] lg:h-[5vw] overflow-hidden flex items-center justify-center animate-slide-left"
            style={{
              bottom: '5vh',
              right: '3vw',
              animationDelay: '0ms',
            }}
          >
            <img
              src={uberEatsIcon}
              alt="Uber Eats"
              className="w-full h-full object-cover"
            />
          </button>
          <button
            onClick={() => handleDeliveryChoice('deliveroo')}
            className="absolute bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border-2 border-transparent hover:border-cyan-400 w-[15vw] h-[15vw] lg:w-[5vw] lg:h-[5vw] overflow-hidden flex items-center justify-center animate-slide-right"
            style={{
              bottom: '15vh',
              right: '7vw',
              animationDelay: '100ms',
            }}
          >
            <img
              src={deliverooIcon}
              alt="Deliveroo"
              className="w-full h-full object-cover"
            />
          </button>
          <button
            onClick={() => setIsExpanded(false)}
            className="absolute text-gray-500 hover:text-gray-600 transition-colors text-xl"
            style={{
              bottom: '22vh',
              right: '3vw',
              animationDelay: '200ms',
            }}
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onClick={handleIconClick}
          className="-rotate-12 hover:scale-125 transition-transform duration-300 animate-shake"
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          aria-label="Delivery"
        >
          <img
            src={randomIcon}
            alt="Delivery Icon"
            className="w-[30vw] h-[30vw] lg:w-[15vw] lg:h-[15vw] object-cover"
          />
        </button>
      )}
    </div>
  );
}
